import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Table,
  Modal,
} from "react-bootstrap";

import {
  ArrowLeft,
  StarFill,
  Image as ImageIcon,
  PlusCircle,
  Search,
  CheckCircleFill,
  ExclamationTriangleFill,
  ArrowClockwise,
  PencilSquare,
  Trash,
  XCircle,
  Save,
  BoxArrowRight,
  EyeFill,
  EyeSlashFill,
} from "react-bootstrap-icons";

import "./AdminProductos.css";

/* =========================================================
   API
========================================================= */

const API =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* =========================================================
   AUTENTICACIÓN
========================================================= */

const obtenerToken = () => {
  return sessionStorage.getItem("gj_admin_token");
};

const obtenerHeadersAuth = () => {
  const token = obtenerToken();

  return {
    Authorization: `Bearer ${token}`,
  };
};

/* =========================================================
   CATEGORÍAS

   MISMAS CATEGORÍAS QUE NUEVOS PRODUCTOS
========================================================= */

const categorias = [
  "Refrigeración",
  "Aire Acondicionado",
  "Lavadoras",
  "Secadoras",
  "Cocinas",
  "Instalaciones a Gas",
  "Reguladores",
  "Accesorios",
  "Herramientas",
  "Eléctrico",
  "Otros",
];

/* =========================================================
   FORMULARIO VACÍO
========================================================= */

const formularioInicial = {
  nombre: "",
  descripcion: "",
  categoria: "",
  grupo: "1",
  orden: "1",
  activo: true,
};

/* =========================================================
   COMPONENTE
========================================================= */

export default function AdminFeaturedProducts() {
  const navigate = useNavigate();

  /* =======================================================
     ESTADOS
  ======================================================= */

  const [productos, setProductos] = useState([]);

  const [form, setForm] = useState(formularioInicial);

  const [imagen, setImagen] = useState(null);

  const [preview, setPreview] = useState("");

  const [productoEditando, setProductoEditando] = useState(null);

  const [productoEliminar, setProductoEliminar] = useState(null);

  const [busqueda, setBusqueda] = useState("");

  const [filtroGrupo, setFiltroGrupo] = useState("todos");

  const [filtroCategoria, setFiltroCategoria] = useState("todas");

  const [cargando, setCargando] = useState(true);

  const [guardando, setGuardando] = useState(false);

  const [eliminando, setEliminando] = useState(false);

  const [mensaje, setMensaje] = useState({
    tipo: "",
    texto: "",
  });

  /* =======================================================
     CERRAR SESIÓN
  ======================================================= */

  const cerrarSesion = () => {
    sessionStorage.removeItem("gj_admin_token");
    sessionStorage.removeItem("gj_admin_usuario");

    navigate("/login", {
      replace: true,
    });
  };

  /* =======================================================
     CARGAR PRODUCTOS
  ======================================================= */

  const cargarProductos = async () => {
    try {
      setCargando(true);

      const response = await fetch(`${API}/admin/featured-products`, {
        headers: obtenerHeadersAuth(),
      });

      if (response.status === 401 || response.status === 403) {
        cerrarSesion();
        return;
      }

      if (!response.ok) {
        throw new Error("No se pudieron cargar los productos destacados.");
      }

      const data = await response.json();

      setProductos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);

      setMensaje({
        tipo: "danger",

        texto:
          error.message || "No se pudieron cargar los productos destacados.",
      });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarProductos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     CAMBIAR INPUTS
  ======================================================= */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* =======================================================
     IMAGEN
  ======================================================= */

  const handleImagen = (e) => {
    const archivo = e.target.files?.[0];

    if (!archivo) {
      setImagen(null);

      if (productoEditando?.imagen) {
        setPreview(productoEditando.imagen);
      } else {
        setPreview("");
      }

      return;
    }

    const tiposPermitidos = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!tiposPermitidos.includes(archivo.type)) {
      setMensaje({
        tipo: "warning",
        texto: "Selecciona una imagen JPG, PNG o WEBP.",
      });

      e.target.value = "";

      return;
    }

    if (archivo.size > 5 * 1024 * 1024) {
      setMensaje({
        tipo: "warning",
        texto: "La imagen no puede superar los 5 MB.",
      });

      e.target.value = "";

      return;
    }

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setImagen(archivo);

    const url = URL.createObjectURL(archivo);

    setPreview(url);
  };

  /* =======================================================
     LIMPIAR FORMULARIO
  ======================================================= */

  const limpiarFormulario = (limpiarMensaje = true) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setForm(formularioInicial);

    setImagen(null);

    setPreview("");

    setProductoEditando(null);

    const inputImagen = document.getElementById("featured-product-image");

    if (inputImagen) {
      inputImagen.value = "";
    }

    if (limpiarMensaje) {
      setMensaje({
        tipo: "",
        texto: "",
      });
    }
  };

  /* =======================================================
     NUEVO
  ======================================================= */

  const nuevoProducto = () => {
    limpiarFormulario();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =======================================================
     EDITAR
  ======================================================= */

  const editarProducto = (producto) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setProductoEditando(producto);

    setForm({
      nombre: producto.nombre || "",

      descripcion: producto.descripcion || "",

      categoria: producto.categoria || "",

      grupo: String(producto.grupo || 1),

      orden: String(producto.orden ?? 0),

      activo: producto.activo !== false,
    });

    setImagen(null);

    setPreview(producto.imagen || "");

    const inputImagen = document.getElementById("featured-product-image");

    if (inputImagen) {
      inputImagen.value = "";
    }

    setMensaje({
      tipo: "",
      texto: "",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =======================================================
     GUARDAR / ACTUALIZAR
  ======================================================= */

  const guardarProducto = async (e) => {
    e.preventDefault();

    if (!form.nombre.trim()) {
      setMensaje({
        tipo: "warning",
        texto: "Ingresa el nombre del producto.",
      });

      return;
    }

    if (!form.categoria) {
      setMensaje({
        tipo: "warning",
        texto: "Selecciona una categoría.",
      });

      return;
    }

    if (!productoEditando && !imagen) {
      setMensaje({
        tipo: "warning",
        texto: "Selecciona una imagen para el producto.",
      });

      return;
    }

    try {
      setGuardando(true);

      setMensaje({
        tipo: "",
        texto: "",
      });

      const datos = new FormData();

      datos.append("nombre", form.nombre.trim());

      datos.append("descripcion", form.descripcion.trim());

      datos.append("categoria", form.categoria);

      datos.append("grupo", form.grupo);

      datos.append("orden", form.orden || "0");

      datos.append("activo", form.activo ? "true" : "false");

      if (imagen) {
        datos.append("imagen", imagen);
      }

      const url = productoEditando
        ? `${API}/admin/featured-products/${productoEditando._id}`
        : `${API}/admin/featured-products`;

      const metodo = productoEditando ? "PUT" : "POST";

      const response = await fetch(url, {
        method: metodo,

        headers: obtenerHeadersAuth(),

        body: datos,
      });

      if (response.status === 401 || response.status === 403) {
        cerrarSesion();
        return;
      }

      let resultado = {};

      try {
        resultado = await response.json();
      } catch {
        resultado = {};
      }

      if (!response.ok) {
        throw new Error(
          resultado.message ||
            (productoEditando
              ? "No se pudo actualizar el producto."
              : "No se pudo guardar el producto."),
        );
      }

      const eraEdicion = Boolean(productoEditando);

      limpiarFormulario(false);

      await cargarProductos();

      setMensaje({
        tipo: "success",

        texto: eraEdicion
          ? "Producto destacado actualizado correctamente."
          : "Producto destacado guardado correctamente.",
      });
    } catch (error) {
      console.error(error);

      setMensaje({
        tipo: "danger",
        texto: error.message,
      });
    } finally {
      setGuardando(false);
    }
  };

  /* =======================================================
     MODAL ELIMINAR
  ======================================================= */

  const confirmarEliminar = (producto) => {
    setProductoEliminar(producto);
  };

  const cerrarEliminar = () => {
    if (eliminando) return;

    setProductoEliminar(null);
  };

  /* =======================================================
     ELIMINAR
  ======================================================= */

  const eliminarProducto = async () => {
    if (!productoEliminar?._id) {
      return;
    }

    try {
      setEliminando(true);

      const response = await fetch(
        `${API}/admin/featured-products/${productoEliminar._id}`,
        {
          method: "DELETE",

          headers: obtenerHeadersAuth(),
        },
      );

      if (response.status === 401 || response.status === 403) {
        cerrarSesion();
        return;
      }

      let resultado = {};

      try {
        resultado = await response.json();
      } catch {
        resultado = {};
      }

      if (!response.ok) {
        throw new Error(
          resultado.message || "No se pudo eliminar el producto.",
        );
      }

      if (productoEditando?._id === productoEliminar._id) {
        limpiarFormulario(false);
      }

      setProductoEliminar(null);

      await cargarProductos();

      setMensaje({
        tipo: "success",
        texto: "Producto destacado eliminado correctamente.",
      });
    } catch (error) {
      console.error(error);

      setMensaje({
        tipo: "danger",
        texto: error.message,
      });
    } finally {
      setEliminando(false);
    }
  };

  /* =======================================================
     PRODUCTOS FILTRADOS
  ======================================================= */

  const productosFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    return productos.filter((producto) => {
      const coincideBusqueda =
        !texto ||
        String(producto.nombre || "")
          .toLowerCase()
          .includes(texto) ||
        String(producto.categoria || "")
          .toLowerCase()
          .includes(texto) ||
        String(producto.descripcion || "")
          .toLowerCase()
          .includes(texto);

      const coincideGrupo =
        filtroGrupo === "todos" || String(producto.grupo) === filtroGrupo;

      const coincideCategoria =
        filtroCategoria === "todas" || producto.categoria === filtroCategoria;

      return coincideBusqueda && coincideGrupo && coincideCategoria;
    });
  }, [productos, busqueda, filtroGrupo, filtroCategoria]);

  /* =======================================================
     CONTADORES
  ======================================================= */

  const totalActivos = useMemo(() => {
    return productos.filter((producto) => producto.activo).length;
  }, [productos]);

  const totalGrupo1 = useMemo(() => {
    return productos.filter((producto) => Number(producto.grupo) === 1).length;
  }, [productos]);

  const totalGrupo2 = useMemo(() => {
    return productos.filter((producto) => Number(producto.grupo) === 2).length;
  }, [productos]);

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="admin-productos-page">
      <Container fluid="xl">
        {/* =================================================
            HEADER
        ================================================= */}

        <div className="admin-productos-header">
          <div>
            <span className="admin-productos-kicker">PÁGINA PRINCIPAL</span>

            <h1>
              <StarFill />
              Productos destacados
            </h1>

            <p>
              Administra los productos que aparecen en los carruseles de la
              página principal.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Badge bg="primary" className="admin-productos-total">
              {productos.length} productos
            </Badge>

            <Badge bg="success">{totalActivos} activos</Badge>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate("/admin")}
            >
              <ArrowLeft className="me-2" />
              Volver al menú
            </Button>

            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={cerrarSesion}
            >
              <BoxArrowRight className="me-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>

        {/* =================================================
            MENSAJE
        ================================================= */}

        {mensaje.texto && (
          <Alert
            variant={mensaje.tipo}
            dismissible
            onClose={() =>
              setMensaje({
                tipo: "",
                texto: "",
              })
            }
          >
            {mensaje.tipo === "success" ? (
              <CheckCircleFill className="me-2" />
            ) : (
              <ExclamationTriangleFill className="me-2" />
            )}

            {mensaje.texto}
          </Alert>
        )}

        <Row className="g-4">
          {/* =================================================
              FORMULARIO
          ================================================= */}

          <Col lg={5}>
            <Card className="admin-productos-card">
              <Card.Body>
                <div className="admin-card-title">
                  <div className="admin-card-icon">
                    {productoEditando ? <PencilSquare /> : <PlusCircle />}
                  </div>

                  <div>
                    <h2>
                      {productoEditando
                        ? "Editar destacado"
                        : "Nuevo destacado"}
                    </h2>

                    <p>
                      {productoEditando
                        ? "Modifica los datos del producto seleccionado."
                        : "Agrega un producto a los carruseles principales."}
                    </p>
                  </div>
                </div>

                {productoEditando && (
                  <Alert variant="primary" className="py-2">
                    <strong>Editando:</strong> {productoEditando.nombre}
                  </Alert>
                )}

                <Form onSubmit={guardarProducto}>
                  {/* NOMBRE */}

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Nombre del producto
                      <span className="campo-requerido"> *</span>
                    </Form.Label>

                    <Form.Control
                      type="text"
                      name="nombre"
                      value={form.nombre}
                      onChange={handleChange}
                      placeholder="Ej. Control Digital MT-512E 2HP"
                      required
                    />
                  </Form.Group>

                  {/* DESCRIPCIÓN */}

                  <Form.Group className="mb-3">
                    <Form.Label>Descripción</Form.Label>

                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="descripcion"
                      value={form.descripcion}
                      onChange={handleChange}
                      placeholder="Descripción breve del producto..."
                    />
                  </Form.Group>

                  {/* CATEGORÍA */}

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Categoría
                      <span className="campo-requerido"> *</span>
                    </Form.Label>

                    <Form.Select
                      name="categoria"
                      value={form.categoria}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Seleccione una categoría</option>

                      {categorias.map((categoria) => (
                        <option key={categoria} value={categoria}>
                          {categoria}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Row>
                    {/* GRUPO */}

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Carrusel</Form.Label>

                        <Form.Select
                          name="grupo"
                          value={form.grupo}
                          onChange={handleChange}
                        >
                          <option value="1">1 - Productos destacados</option>

                          <option value="2">2 - Más productos</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {/* ORDEN */}

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Orden</Form.Label>

                        <Form.Control
                          type="number"
                          min="0"
                          name="orden"
                          value={form.orden}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* ACTIVO */}

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="featured-activo"
                      name="activo"
                      checked={form.activo}
                      onChange={handleChange}
                      label={
                        form.activo
                          ? "Visible en la página principal"
                          : "Oculto en la página principal"
                      }
                    />
                  </Form.Group>

                  {/* IMAGEN */}

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Imagen del producto
                      {!productoEditando && (
                        <span className="campo-requerido"> *</span>
                      )}
                    </Form.Label>

                    <Form.Control
                      id="featured-product-image"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleImagen}
                    />

                    {productoEditando && (
                      <Form.Text className="text-muted">
                        Déjalo vacío para conservar la imagen actual.
                      </Form.Text>
                    )}
                  </Form.Group>

                  {/* PREVIEW */}

                  {preview ? (
                    <div className="admin-imagen-preview">
                      <img src={preview} alt="Vista previa" />
                    </div>
                  ) : (
                    <div className="admin-imagen-vacia">
                      <ImageIcon />

                      <span>Vista previa de la imagen</span>
                    </div>
                  )}

                  {/* BOTONES */}

                  <div className="admin-form-actions">
                    {productoEditando ? (
                      <>
                        <Button
                          type="button"
                          variant="light"
                          onClick={() => limpiarFormulario()}
                          disabled={guardando}
                        >
                          <XCircle className="me-2" />
                          Cancelar
                        </Button>

                        <Button
                          type="submit"
                          variant="primary"
                          disabled={guardando}
                        >
                          {guardando ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Actualizando...
                            </>
                          ) : (
                            <>
                              <Save className="me-2" />
                              Guardar cambios
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="light"
                          onClick={nuevoProducto}
                          disabled={guardando}
                        >
                          Limpiar
                        </Button>

                        <Button
                          type="submit"
                          variant="primary"
                          disabled={guardando}
                        >
                          {guardando ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <PlusCircle className="me-2" />
                              Guardar destacado
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* =================================================
              LISTADO
          ================================================= */}

          <Col lg={7}>
            <Card className="admin-productos-card">
              <Card.Body>
                <div className="admin-list-header">
                  <div>
                    <h2>Productos del carrusel</h2>

                    <p>
                      Grupo 1: {totalGrupo1} · Grupo 2: {totalGrupo2}
                    </p>
                  </div>

                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={cargarProductos}
                    disabled={cargando}
                    title="Actualizar listado"
                  >
                    <ArrowClockwise />
                  </Button>
                </div>

                {/* BUSCADOR */}

                <div className="admin-search mb-3">
                  <Search />

                  <Form.Control
                    type="text"
                    placeholder="Buscar producto o categoría..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </div>

                {/* FILTROS */}

                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Select
                      value={filtroGrupo}
                      onChange={(e) => setFiltroGrupo(e.target.value)}
                    >
                      <option value="todos">Todos los carruseles</option>

                      <option value="1">Productos destacados</option>

                      <option value="2">Más productos</option>
                    </Form.Select>
                  </Col>

                  <Col md={6}>
                    <Form.Select
                      value={filtroCategoria}
                      onChange={(e) => setFiltroCategoria(e.target.value)}
                    >
                      <option value="todas">Todas las categorías</option>

                      {categorias.map((categoria) => (
                        <option key={categoria} value={categoria}>
                          {categoria}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                {/* TABLA */}

                {cargando ? (
                  <div className="admin-productos-loading-list">
                    <Spinner animation="border" size="sm" />

                    <span>Cargando productos...</span>
                  </div>
                ) : (
                  <div className="admin-productos-table">
                    <Table hover responsive className="align-middle">
                      <thead>
                        <tr>
                          <th>Imagen</th>

                          <th>Producto</th>

                          <th>Grupo</th>

                          <th>Orden</th>

                          <th>Estado</th>

                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>

                      <tbody>
                        {productosFiltrados.length === 0 ? (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center py-5 text-muted"
                            >
                              No hay productos para mostrar.
                            </td>
                          </tr>
                        ) : (
                          productosFiltrados.map((producto) => (
                            <tr key={producto._id}>
                              <td>
                                <img
                                  className="admin-tabla-imagen"
                                  src={producto.imagen}
                                  alt={producto.nombre || "Producto"}
                                  loading="lazy"
                                />
                              </td>

                              <td>
                                <div className="admin-product-name">
                                  {producto.nombre || "Sin nombre"}
                                </div>

                                <small>
                                  {producto.categoria || "Sin categoría"}
                                </small>
                              </td>

                              <td>
                                <Badge
                                  bg={
                                    Number(producto.grupo) === 1
                                      ? "primary"
                                      : "secondary"
                                  }
                                >
                                  Grupo {producto.grupo}
                                </Badge>
                              </td>

                              <td>{producto.orden ?? 0}</td>

                              <td>
                                {producto.activo ? (
                                  <Badge bg="success">
                                    <EyeFill className="me-1" />
                                    Visible
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary">
                                    <EyeSlashFill className="me-1" />
                                    Oculto
                                  </Badge>
                                )}
                              </td>

                              <td>
                                <div className="d-flex justify-content-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline-primary"
                                    size="sm"
                                    title="Editar producto"
                                    onClick={() => editarProducto(producto)}
                                  >
                                    <PencilSquare />
                                  </Button>

                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    title="Eliminar producto"
                                    onClick={() => confirmarEliminar(producto)}
                                  >
                                    <Trash />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ===================================================
          MODAL ELIMINAR
      =================================================== */}

      <Modal
        show={Boolean(productoEliminar)}
        onHide={cerrarEliminar}
        centered
        backdrop={eliminando ? "static" : true}
        keyboard={!eliminando}
      >
        <Modal.Header closeButton={!eliminando}>
          <Modal.Title>Eliminar producto destacado</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {productoEliminar && (
            <>
              {productoEliminar.imagen && (
                <div className="text-center mb-3">
                  <img
                    src={productoEliminar.imagen}
                    alt={productoEliminar.nombre || "Producto"}
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "contain",
                      border: "1px solid #dee2e6",
                      borderRadius: "10px",
                      padding: "6px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              )}

              <p>¿Está seguro de eliminar este producto destacado?</p>

              <div className="border rounded p-3 bg-light">
                <strong>{productoEliminar.nombre}</strong>

                <div className="small text-muted mt-1">
                  {productoEliminar.categoria || "Sin categoría"}
                </div>

                <div className="small text-muted">
                  Carrusel: Grupo {productoEliminar.grupo}
                </div>
              </div>

              <Alert variant="warning" className="mt-3 mb-0">
                El producto se eliminará de MongoDB y su imagen también será
                eliminada de Cloudinary.
              </Alert>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={cerrarEliminar}
            disabled={eliminando}
          >
            Cancelar
          </Button>

          <Button
            variant="danger"
            onClick={eliminarProducto}
            disabled={eliminando}
          >
            {eliminando ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash className="me-2" />
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
